import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { bookingDates, isWeekendOrHoliday, formatDateDE as fmtDate, toYMDLocal } from "@/lib/utils";
import { MWST_RATE, MWST_RATE_PERCENT } from "@/lib/vatConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) =>
  (Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export default function AnfrageAnnehmen() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token") || "";

  const [inq, setInq] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error | success
  const [errMsg, setErrMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // Editable fields
  const [hourlyRate, setHourlyRate] = useState(49);
  const [weekendRate, setWeekendRate] = useState(59);
  const [applySpontan, setApplySpontan] = useState(false);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [days, setDays] = useState(1);
  const [notes, setNotes] = useState("");
  const [confNumber, setConfNumber] = useState("");
  const [depositPercent, setDepositPercent] = useState(0);
  const [depositDue, setDepositDue] = useState(
    toYMDLocal(new Date(Date.now() + 7 * 86400000)),
  );
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [weekendDates, setWeekendDates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/inquiries/by-action-token/${id}?token=${encodeURIComponent(token)}`,
        );
        setInq(data);
        setHourlyRate(data.hourly_rate_eur || 49);
        setWeekendRate(data.hourly_rate_weekend_eur || 59);
        setApplySpontan(!!data.is_spontaneous);
        setHoursPerDay(parseFloat((data.einsatzdauer || "8").replace(/[^\d.]/g, "")) || 8);
        setDays(data.anzahl_tage || 1);
        setConfNumber(data.confirmation_number || "");
        setDepositPercent(data.deposit_percent || 0);
        setVon(data.von_uhrzeit || "");
        setBis(data.bis_uhrzeit || "");
        if (data.status === "confirmed") {
          setStatus("already");
        } else {
          setStatus("ok");
        }
      } catch (err) {
        setErrMsg(err?.response?.data?.detail || "Der Link ist ungültig oder wurde bereits verwendet.");
        setStatus("error");
      }
    })();
  }, [id, token]);

  const dayList = useMemo(
    () => (inq ? bookingDates(inq.einsatzdatum, Number(days) || 1) : []),
    [inq, days],
  );

  useEffect(() => {
    if (!dayList.length) return;
    setWeekendDates(
      dayList.filter((d) => (inq?.weekend_dates || []).includes(d) || isWeekendOrHoliday(d)),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayList.join(",")]);

  const toggleWeekendDay = (d) =>
    setWeekendDates((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const effRate = (Number(hourlyRate) || 0) * (applySpontan ? 1.10 : 1);
  const effWeRate = (Number(weekendRate) || 0) * (applySpontan ? 1.10 : 1);
  const weDaysCount = weekendDates.length;
  const wdDaysCount = Math.max(0, dayList.length - weDaysCount);
  const labor = (Number(hoursPerDay) || 0) * (wdDaysCount * effRate + weDaysCount * effWeRate);
  const net = labor;
  const netCents = Math.round(net * 100);
  const mwst = Math.round(netCents * MWST_RATE) / 100;
  const gross = Math.round((net + mwst) * 100) / 100;
  const depositAmount = depositPercent > 0 ? Math.round(gross * depositPercent) / 100 : 0;

  const handleConfirm = async () => {
    if (!hourlyRate || !hoursPerDay) {
      toast.error("Stundensatz und Stunden pro Tag müssen gesetzt sein");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/inquiries/by-action-token/${id}/confirm-binding?token=${encodeURIComponent(token)}`,
        {
          hourly_rate_eur: Number(effRate.toFixed(2)),
          hourly_rate_weekend_eur: Number(effWeRate.toFixed(2)),
          weekend_dates: weekendDates,
          hours_per_day: Number(hoursPerDay),
          anzahl_tage: Number(days),
          travel_cost_eur: 0,
          notes,
          confirmed_von: von,
          confirmed_bis: bis,
          confirmation_number: confNumber,
          deposit_percent: Number(depositPercent),
          deposit_due_date: Number(depositPercent) > 0 ? depositDue : "",
        },
      );
      setStatus("success");
      toast.success("Auftrag verbindlich bestätigt – E-Mail an Kunde gesendet");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Bestätigen");
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <FullPageState>
        <Loader2 size={40} className="mx-auto animate-spin text-[#BA6D52]" />
        <p className="mt-6 text-sm text-[#757067]">Anfrage wird geladen …</p>
      </FullPageState>
    );
  }
  if (status === "error") {
    return (
      <FullPageState>
        <XCircle size={56} className="mx-auto text-red-500" />
        <h1 className="font-serif text-3xl font-light tracking-tighter mt-6">Link ungültig</h1>
        <p className="mt-4 text-[#757067] font-light">{errMsg}</p>
      </FullPageState>
    );
  }
  if (status === "success") {
    return (
      <FullPageState>
        <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
        <h1 className="font-serif text-3xl font-light tracking-tighter mt-6">Auftrag bestätigt ✓</h1>
        <p className="mt-4 text-[#757067] font-light">
          <b>{inq?.betrieb}</b> am <b>{fmtDate(inq?.einsatzdatum)}</b> ist verbindlich angenommen.
          Der Kunde hat eine Auftragsbestätigung per E-Mail erhalten
          {depositPercent > 0 && (
            <> — inkl. Zahlungsanweisung für die Anzahlung von {eur(depositAmount)}</>
          )}
          . Der Tag wurde im Kalender rot markiert.
        </p>
        <Link
          to="/admin"
          className="mt-8 inline-block text-xs uppercase tracking-[0.25em] text-[#BA6D52] hover:text-[#9E5841] underline"
        >
          Zum Admin-Portal
        </Link>
      </FullPageState>
    );
  }
  if (status === "already") {
    return (
      <FullPageState>
        <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
        <h1 className="font-serif text-3xl font-light tracking-tighter mt-6">Bereits bestätigt</h1>
        <p className="mt-4 text-[#757067] font-light">
          Diese Anfrage wurde bereits verbindlich angenommen. Aktuelle Auftragsdetails findest du im Admin-Portal.
        </p>
        <Link
          to="/admin"
          className="mt-8 inline-block text-xs uppercase tracking-[0.25em] text-[#BA6D52] hover:text-[#9E5841] underline"
        >
          Zum Admin-Portal
        </Link>
      </FullPageState>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-10 md:py-14 px-4 md:px-8" data-testid="anfrage-annehmen-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Anfrage verbindlich annehmen</p>
          <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tighter mt-3 text-[#2C2A26]">
            {inq.betrieb}
          </h1>
          <p className="mt-2 text-sm text-[#757067] font-light">
            📅 {fmtDate(inq.einsatzdatum)}
            {days > 1 && ` (${days} Tage)`}
            {" · "}
            📍 {inq.ort}
            {" · "}
            📞 {inq.telefon}
          </p>
        </div>

        {/* Formular */}
        <div className="bg-white border border-[#E3DECE] p-6 md:p-8 space-y-8">
          {/* Uhrzeit */}
          <FormGroup title="Uhrzeit">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Von">
                <input
                  type="time"
                  value={von}
                  onChange={(e) => setVon(e.target.value)}
                  data-testid="edit-von"
                  className={inputCls}
                />
              </FormField>
              <FormField label="Bis">
                <input
                  type="time"
                  value={bis}
                  onChange={(e) => setBis(e.target.value)}
                  data-testid="edit-bis"
                  className={inputCls}
                />
              </FormField>
            </div>
          </FormGroup>

          {/* Anzahl Tage + Stunden pro Tag */}
          <FormGroup title="Umfang">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Anzahl Tage">
                <input
                  type="number"
                  min="1"
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value) || 1)}
                  data-testid="edit-days"
                  className={inputCls}
                />
              </FormField>
              <FormField label="Stunden pro Tag">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={hoursPerDay}
                  onChange={(e) => setHoursPerDay(Number(e.target.value) || 0)}
                  data-testid="edit-hours"
                  className={inputCls}
                />
              </FormField>
            </div>
          </FormGroup>

          {/* Stundensätze */}
          <FormGroup title="Stundensätze">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={`Werktag${wdDaysCount > 0 ? ` (${wdDaysCount} Tag${wdDaysCount > 1 ? "e" : ""})` : ""}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value) || 0)}
                    data-testid="edit-rate-wd"
                    className={inputCls}
                  />
                  <span className="text-[#757067]">€/h</span>
                </div>
              </FormField>
              <FormField label={`Wochenende/Feiertag${weDaysCount > 0 ? ` (${weDaysCount} Tag${weDaysCount > 1 ? "e" : ""})` : ""}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={weekendRate}
                    onChange={(e) => setWeekendRate(Number(e.target.value) || 0)}
                    data-testid="edit-rate-we"
                    className={inputCls}
                  />
                  <span className="text-[#757067]">€/h</span>
                </div>
              </FormField>
            </div>
            <label className="mt-3 flex items-center gap-2 cursor-pointer text-sm text-[#757067]">
              <input
                type="checkbox"
                checked={applySpontan}
                onChange={(e) => setApplySpontan(e.target.checked)}
                data-testid="edit-spontan"
                className="w-4 h-4 accent-[#BA6D52]"
              />
              <span>+10 % Spontanbuchungs-Aufschlag anwenden</span>
            </label>
          </FormGroup>

          {/* WE-Tag-Toggle bei Mehrtagesbuchung */}
          {dayList.length > 1 && (
            <FormGroup title="Wochenende/Feiertag pro Tag">
              <p className="text-xs text-[#757067] mb-3">
                Klick auf einen Tag um zwischen Werktag-Satz und Wochenende-Satz zu wechseln.
              </p>
              <div className="flex flex-wrap gap-2">
                {dayList.map((d) => {
                  const isWe = weekendDates.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleWeekendDay(d)}
                      data-testid={`edit-day-${d}`}
                      className={`text-xs px-3 py-1.5 border transition-colors ${
                        isWe
                          ? "bg-[#BA6D52] text-white border-[#BA6D52]"
                          : "bg-white text-[#2C2A26] border-[#E3DECE] hover:border-[#BA6D52]"
                      }`}
                    >
                      {fmtDate(d)}
                      <span className="ml-2 text-[10px] opacity-80">
                        {isWe ? "WE/Feiertag" : "Werktag"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </FormGroup>
          )}


          {/* Anzahlung */}
          <FormGroup title="Anzahlung">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {[0, 25, 50, 100].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setDepositPercent(p)}
                  data-testid={`edit-deposit-${p}`}
                  className={`text-xs px-4 py-2 border transition-colors ${
                    depositPercent === p
                      ? "bg-[#BA6D52] text-white border-[#BA6D52]"
                      : "bg-white text-[#2C2A26] border-[#E3DECE] hover:border-[#BA6D52]"
                  }`}
                >
                  {p === 0 ? "Keine" : `${p} %`}
                </button>
              ))}
              <input
                type="number"
                min="0"
                max="100"
                value={[0, 25, 50, 100].includes(Number(depositPercent)) ? "" : depositPercent}
                onChange={(e) => setDepositPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                placeholder="individuell"
                data-testid="edit-deposit-custom"
                className={`${inputCls} w-24 text-center`}
              />
              <span className="text-sm text-[#757067]">%</span>
            </div>
            {depositPercent > 0 && (
              <FormField label="Zahlungsziel Anzahlung">
                <input
                  type="date"
                  value={depositDue}
                  onChange={(e) => setDepositDue(e.target.value)}
                  data-testid="edit-deposit-due"
                  className={inputCls}
                />
              </FormField>
            )}
          </FormGroup>

          {/* Auftragsnummer + Notizen */}
          <FormGroup title="Zusätzliches">
            <FormField label="Auftragsnummer (optional)">
              <input
                type="text"
                value={confNumber}
                onChange={(e) => setConfNumber(e.target.value)}
                placeholder="Auto-Generierung wenn leer"
                data-testid="edit-conf-number"
                className={inputCls}
              />
            </FormField>
            <FormField label="Notiz an Kunden (optional)">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="z.B. Sonderwünsche, Ausrüstung, Menü-Details …"
                data-testid="edit-notes"
                className={`${inputCls} resize-none`}
              />
            </FormField>
          </FormGroup>
        </div>

        {/* Live-Kostenvorschau */}
        <div className="mt-6 bg-[#FAF5F1] border border-[#BA6D52] p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] font-semibold mb-4">Kostenvorschau</p>
          <div className="space-y-2 text-sm">
            <Line label={`Arbeitsleistung (${wdDaysCount} WT × ${hoursPerDay}h × ${eur(effRate)} + ${weDaysCount} WE × ${hoursPerDay}h × ${eur(effWeRate)})`} value={eur(labor)} />
            <Line label="Netto" value={eur(net)} bold />
            <Line label={`+ ${MWST_RATE_PERCENT} % MwSt`} value={eur(mwst)} muted />
            <Line label="= Gesamtsumme (brutto)" value={eur(gross)} highlight />
            {depositPercent > 0 && (
              <>
                <div className="border-t border-[#E3DECE] pt-2 mt-2" />
                <Line label={`Anzahlung (${depositPercent} % vom Brutto)`} value={eur(depositAmount)} muted />
                <Line label="Restbetrag nach Einsatz" value={eur(gross - depositAmount)} muted />
              </>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleConfirm}
            disabled={busy}
            data-testid="submit-confirm-binding"
            className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-6 py-4 text-sm uppercase tracking-[0.2em] inline-flex items-center justify-center gap-3"
          >
            {busy ? (
              <><Loader2 size={16} className="animate-spin" /> Wird bestätigt …</>
            ) : (
              <>✓ Verbindlich annehmen &amp; E-Mail senden</>
            )}
          </button>
          <Link
            to="/admin"
            className="border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26] hover:text-white px-6 py-4 text-sm uppercase tracking-[0.2em] text-center transition-colors"
          >
            Zum Admin
          </Link>
        </div>

        <p className="mt-6 text-xs text-[#757067] italic text-center">
          Nach dem Bestätigen wird eine PDF-Auftragsbestätigung + E-Mail an den Kunden gesendet.
          Der Einsatztag wird automatisch im Kalender als {`„Ausgebucht"`} markiert.
        </p>
      </div>
    </div>
  );
}

// ---------- Reusable form pieces ----------
const inputCls = "w-full border border-[#E3DECE] px-3 py-2 text-sm bg-white focus:border-[#BA6D52] focus:outline-none";

function FormGroup({ title, children }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] font-semibold mb-3">{title}</h3>
      <div>{children}</div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="mb-3">
      <label className="text-xs text-[#757067] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Line({ label, value, bold, muted, highlight }) {
  return (
    <div className={`flex justify-between items-baseline ${highlight ? "text-lg font-serif text-[#BA6D52]" : ""}`}>
      <span className={`${muted ? "text-[#757067]" : "text-[#2C2A26]"} ${bold ? "font-medium" : ""}`}>{label}</span>
      <span className={`${muted ? "text-[#757067]" : "text-[#2C2A26]"} ${bold ? "font-semibold" : ""} tabular-nums`}>{value}</span>
    </div>
  );
}

function FullPageState({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
      <div className="max-w-lg w-full bg-white border border-[#E3DECE] p-8 md:p-12 text-center">
        {children}
      </div>
    </div>
  );
}
